import { useEffect, useState } from 'react';

export default function AskQuestionModal({
  open,
  onClose,
  onPost,
  title = 'Ask Your Questions',
  placeholder = 'Type your Questions or Doubts...',
  submitLabel = 'Post',
  initialValue = '',
  submitting = false,
}) {
  const [question, setQuestion] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (open) setQuestion(initialValue);
    if (!open) setQuestion('');
  }, [open, initialValue]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || submitting) return;
    await onPost?.(trimmed);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-question-title"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-[420px] rounded-3xl bg-white p-6 shadow-2xl sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="ask-question-title" className="text-xl font-bold text-[#1B1B1B] sm:text-2xl">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D9D9D9] text-[#666666] transition hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6">
          <textarea
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder={placeholder}
            rows={6}
            disabled={submitting}
            className="w-full resize-none rounded-xl border border-[#D9D9D9] px-4 py-4 text-sm text-[#1B1B1B] outline-none transition placeholder:text-[#AAAAAA] focus:border-[#9369CC] focus:ring-1 focus:ring-[#9369CC] disabled:bg-[#F8F8F8]"
          />

          <div className="mt-6 flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="w-full rounded-full border border-[#666666] px-8 py-2.5 text-sm font-semibold text-[#666666] transition hover:bg-[#F8F8F8] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!question.trim() || submitting}
              className="w-full rounded-full bg-gradient-to-r from-[#6A74E1] to-[#B539FD] px-8 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
